<template>
    <SidePanel
        :visible="notificationStore.isPanelVisible"
        :title="panelTitle"
        size="panel-md"
        @close="closePanel"
    >
        <div v-if="notificationStore.activeTab === 'VIEW'">
            <Notification
                :alert="notificationStore.currentAlert"
                @clear="
                    openModal(
                        'deleteNotification',
                        notificationStore.currentAlert
                    )
                "
                @view="routeNotification"
            ></Notification>
        </div>

        <div
            v-else
            class="d-flex flex-column mb-3"
            style="gap: var(--space-3, 0.75rem)"
        >
            <Alert
                v-for="alert in getAllAlerts"
                :key="alert.id"
                class="mb-0"
                :title="alert.title"
                :type="alert.type"
                :id="alert.id"
                :showClearBtn="alert.showClearBtn === false ? false : true"
                :actionButtons="[
                    {
                        click: 'view',
                        label: 'View',
                        icon: 'fi-eye',
                        class: 'btn-primary',
                    },
                ]"
                @clear="openModal('deleteNotification', alert)"
                @view="viewNotification(alert)"
                @goToPlans="goToPlans"
            >
                <span v-html="alert.message"></span>
            </Alert>

            <div v-if="!getAllAlerts.length" class="text-center">
                No notifications to display
            </div>
        </div>

        <template #footer>
            <div
                v-if="getAllAlerts.length"
                class="p-3 border-top bottom-element"
            >
                <div
                    v-if="notificationStore.activeTab === 'LIST'"
                    class="d-flex justify-content-end"
                >
                    <button
                        class="btn btn-primary"
                        @click="openModal('clearAll')"
                    >
                        Clear Notifications
                    </button>
                </div>
                <button v-else class="btn btn-primary" @click="returntoAlerts">
                    <i class="fi fi-chevron-left"></i>
                    All Notifications
                </button>
            </div>
        </template>
    </SidePanel>

    <!-- Delete Notification Modal -->
    <Modal :visible="isModalVisible" @close="closeModal">
        <div class="d-flex flex-column gap-2 align-items-center">
            <i
                class="fi fi-lg fi-alert-circle"
                style="
                    font-size: 2.125rem;
                    line-height: 2.125rem;
                    color: var(--danger);
                "
            ></i>
            <h2>{{ modalTitle }}</h2>
            <p style="color: var(--neutral-800)">
                {{ modalMessage }}
            </p>
            <div>
                <button
                    type="button"
                    class="btn btn-desktop-lg btn-white me-2"
                    @click="closeModal"
                >
                    No
                </button>
                <button
                    type="button"
                    class="btn btn-desktop-lg btn-primary"
                    @click="modalAction"
                >
                    Delete
                </button>
            </div>
        </div>
    </Modal>
</template>

<script>
import SidePanel from './SidePanel.vue';
import Alert from './Alert.vue';
import Notification from './Notification.vue';
import Modal from './Modal.vue';
import { useNotificationStore } from '@/stores/notification';
import { goToPlans as navigateToPlans } from '@/utils/navigation';
import { sanitizeText } from '@/utils/textUtils';

export default {
    name: 'NotificationPanel',
    components: {
        SidePanel,
        Alert,
        Notification,
        Modal,
    },
    data() {
        return {
            alerts: [],
            notificationStore: null,
            isModalVisible: false,
            modalTitle: '',
            modalMessage: '',
            modalType: '',
            selectedAlert: null,
        };
    },
    computed: {
        panelTitle() {
            return this.notificationStore.activeTab === 'VIEW'
                ? 'View Notification'
                : 'Notifications';
        },

        freeTrialPersistentNotification() {
            return this.$permStore.freeTrialPersistentNotification;
        },

        getAllAlerts() {
            if (this.freeTrialPersistentNotification) {
                return [this.freeTrialPersistentNotification, ...this.alerts];
            }

            return this.alerts;
        },
    },
    methods: {
        closePanel() {
            this.notificationStore.closePanel();
        },

        returntoAlerts() {
            this.notificationStore.openList();
        },

        dismissAllNotifications() {
            this.$postData('notification/dismiss-all')
                .then((response) => {
                    if (response?.success) {
                        this.closePanel();
                        this.getNotifications();
                    }
                })
                .finally(() => {
                    this.isModalVisible = false;
                });
        },

        dismissNotification(alertID) {
            this.$postData('notification/dismiss', { id: alertID })
                .then((response) => {
                    if (response?.success) {
                        this.getNotifications();
                    }
                })
                .finally(() => {
                    this.isModalVisible = false;
                    this.returntoAlerts();
                });
        },

        viewNotification(alert) {
            this.notificationStore.openView(alert);
        },

        getNotifications() {
            this.$getData('notification/read', {
                where: { is_dismissed: false },
                order_by: { created_at: 'desc' },
            }).then((response) => {
                if (response?.success) {
                    const alerts = response.data;
                    this.alerts = alerts.map((alert) => ({
                        ...alert,
                        message: sanitizeText(alert.message),
                    }));
                } else {
                    this.alerts = [];
                }
            });
        },

        routeNotification(alert) {
            this.closePanel();
            this.$router.push(alert.internal_link);
        },

        openModal(type, alert = null) {
            this.modalType = type;
            if (type === 'deleteNotification') {
                this.modalTitle = 'Delete Notification?';
                this.modalMessage =
                    'Are you sure you want to delete this notification?';
                this.selectedAlert = alert;
            } else if (type === 'clearAll') {
                this.modalTitle = 'Clear All Notifications?';
                this.modalMessage =
                    'Are you sure you want to clear all notifications?';
            }
            this.isModalVisible = true;
        },

        modalAction() {
            if (this.modalType === 'deleteNotification') {
                this.dismissNotification(this.selectedAlert.id);
            } else if (this.modalType === 'clearAll') {
                this.dismissAllNotifications();
            }
        },

        closeModal() {
            this.isModalVisible = false;
            this.modalTitle = '';
            this.modalMessage = '';
            this.modalType = '';
            this.selectedAlert = null;
        },

        goToPlans(actionType = 'openPayment') {
            navigateToPlans(this.$router, { actionType });
            this.closePanel();
        },
    },

    mounted() {
        this.getNotifications();
    },
    created() {
        this.notificationStore = useNotificationStore();
    },
};
</script>

<style scoped></style>
